// Research (Blue Market only): what we know about a client (metrics and notes, each with its source), and research
// sessions: Claude on the web only, interactive, findings accepted one by one. Cost shown per session.
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from '../api';
import { Icon, Modal, ago, useConfirm, useToast } from '../ui';
import type { Client } from '../types';

interface Fact { id: string; kind: 'metric' | 'note'; label: string; value: string; source_url: string | null; from_research: boolean; created_by: string; updated_at: string }
interface Finding { id: string; kind: 'metric' | 'fact' | 'person'; label: string; value: string; source: string | null; checked: boolean; state: 'new' | 'accepted' | 'dismissed' }
interface Turn { id: string; number: number; prompt: string; status: 'queued' | 'running' | 'ok' | 'failed' | 'stopped'; answer_html: string; steps: string[]; findings: Finding[];
                 cost_usd: number; error: string; asked_by: string; created_at: string; finished_at: string | null }
interface SessionRow { id: string; title: string; budget_usd: number; cost_usd: number; questions: number; busy: boolean; new_findings: number; created_by: string; updated_at: string }
interface SessionFull extends SessionRow { turns: Turn[]; can_run: boolean; can_edit: boolean; left_usd: number }
interface ResearchData { sessions: SessionRow[]; facts: Fact[]; can_run: boolean; can_edit: boolean; default_budget: number; metric_labels: string[] }

const usd = (n: number) => `$${n.toFixed(2)}`;
const host = (url: string) => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; } };
const KIND: Record<Finding['kind'], string> = { metric: 'Metric', fact: 'Note', person: 'Person' };

function Source({ url, checked = true }: { url: string | null; checked?: boolean }) {
  if (!url) return <span className="muted tiny">no source</span>;
  return <a className={`source tiny ${checked ? '' : 'unchecked'}`} href={url} target="_blank" rel="noopener noreferrer nofollow"
            title={checked ? url : `${url}\nClaude didn't read this page in this session: check it before relying on it.`}>{host(url)}{!checked && ' (not checked)'}</a>;
}

/** Key facts for the client overview: a few metrics, only when there are some. */
export function KeyFacts({ client, go }: { client: Client; go: (tab: string) => void }) {
  const { data } = useQuery({ queryKey: ['client-research', client.id], queryFn: () => get<ResearchData>(`/api/clients/${client.id}/research`) });
  const metrics = (data?.facts ?? []).filter((f) => f.kind === 'metric');
  if (!metrics.length) return null;
  return (
    <section className="card pad stack" style={{ gap: 8 }}>
      <div className="row"><h2 className="grow">Key facts</h2><button className="link-btn small" onClick={() => go('research')}>All</button></div>
      <dl className="facts compact">{metrics.slice(0, 6).map((f) => <div key={f.id}><dt>{f.label}</dt><dd>{f.value}</dd></div>)}</dl>
    </section>
  );
}

export function ResearchTab({ client }: { client: Client }) {
  const [search, setSearch] = useSearchParams();
  const open = search.get('session');
  const show = (id: string | null) => setSearch(id ? { tab: 'research', session: id } : { tab: 'research' });
  const { data } = useQuery({ queryKey: ['client-research', client.id], queryFn: () => get<ResearchData>(`/api/clients/${client.id}/research`) });
  const [starting, setStarting] = useState(false);
  if (!data) return <span className="spin muted" />;
  if (open) return <SessionView id={open} client={client} onBack={() => show(null)} />;
  return (
    <div className="stack" style={{ gap: 16 }}>
      <FactsCard client={client} data={data} />
      <section className="card">
        <div className="card-head"><h2>Research sessions</h2><span className="grow" />
          {data.can_run && <button className="btn sm primary" onClick={() => setStarting(true)}><Icon name="spark" className="ico sm" />Research {client.name}</button>}</div>
        {data.sessions.map((s) => (
          <button key={s.id} className="contact-row" onClick={() => show(s.id)}>
            <span className="grow" style={{ minWidth: 0 }}>
              <span className="row wrap" style={{ gap: 6 }}><b className="small">{s.title}</b>
                {s.busy && <span className="badge info tiny-badge"><span className="spin" /> Working</span>}
                {s.new_findings > 0 && <span className="badge brand tiny-badge">{s.new_findings} to review</span>}</span>
              <span className="muted tiny">{s.questions} question{s.questions === 1 ? '' : 's'} · {s.created_by} · {ago(s.updated_at)}</span>
            </span>
            <span className="muted tiny">{usd(s.cost_usd)} of {usd(s.budget_usd)}</span>
          </button>
        ))}
        {!data.sessions.length && <div className="empty small">No research yet. {data.can_run ? 'Claude searches the web for what matters before a first conversation, with a source for every finding.' : ''}</div>}
      </section>
      {starting && <StartDialog client={client} budget={data.default_budget} onClose={() => setStarting(false)} onStarted={(id) => { setStarting(false); show(id); }} />}
    </div>
  );
}

function StartDialog({ client, budget, onClose, onStarted }: { client: Client; budget: number; onClose: () => void; onStarted: (id: string) => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [focus, setFocus] = useState('');
  const [limit, setLimit] = useState(String(budget));
  const start = useMutation({
    mutationFn: () => post<SessionFull>(`/api/clients/${client.id}/research`, { focus, budget_usd: Number(limit) || budget }),
    onSuccess: (s) => { qc.invalidateQueries({ queryKey: ['client-research', client.id] }); qc.setQueryData(['research-session', s.id], s); onStarted(s.id); },
    onError: (e: Error) => toast(e.message, true),
  });
  return (
    <Modal title={`Research ${client.name}`} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); start.mutate(); }}>
        <p className="small" style={{ margin: 0 }}>Claude searches the web for an overview: what they do, size, news, their apps and websites, technology, and who leads digital.
          {!client.website && <span className="muted"> Adding their website on the Overview tab helps.</span>}</p>
        <label className="field"><span>Anything in particular? (optional)</span>
          <textarea className="input" rows={2} value={focus} onChange={(e) => setFocus(e.target.value)} maxLength={2000} autoFocus placeholder="e.g. their app reviews, who their current agency is" /></label>
        <label className="field"><span>Budget for this session (US$)</span>
          <input className="input" type="number" min={0.5} max={100} step={0.5} value={limit} onChange={(e) => setLimit(e.target.value)} style={{ maxWidth: 120 }} />
          <span className="hint">It stops when this is used up. You can raise it later.</span></label>
        <p className="muted tiny" style={{ margin: 0 }}>Sealed: Claude can only search and read the web. Nothing it reads can change anything here; you choose which findings to keep.</p>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={start.isPending}>{start.isPending && <span className="spin" />}Start</button></div>
      </form>
    </Modal>
  );
}

// ---------- what we know ----------

function FactsCard({ client, data }: { client: Client; data: ResearchData }) {
  const [editing, setEditing] = useState<Fact | 'metric' | 'note' | null>(null);
  const metrics = data.facts.filter((f) => f.kind === 'metric'), notes = data.facts.filter((f) => f.kind === 'note');
  const [allNotes, setAllNotes] = useState(false);
  return (
    <section className="card">
      <div className="card-head"><h2>What we know</h2><span className="grow" />
        {data.can_edit && <button className="btn sm ghost" onClick={() => setEditing('metric')}><Icon name="plus" className="ico sm" />Add</button>}</div>
      {metrics.length > 0 && (
        <dl className="facts">
          {metrics.map((f) => (
            <div key={f.id} className={data.can_edit ? 'editable' : ''} onClick={() => data.can_edit && setEditing(f)}>
              <dt>{f.label}</dt><dd>{f.value} {f.source_url && <Source url={f.source_url} />}</dd>
            </div>
          ))}
        </dl>
      )}
      {notes.length > 0 && <div className="list-label">Notes</div>}
      {(allNotes ? notes : notes.slice(0, 5)).map((f) => (
        <button key={f.id} className="contact-row" onClick={() => data.can_edit && setEditing(f)}>
          <span className="grow small"><b>{f.label}:</b> {f.value} {f.source_url && <Source url={f.source_url} />}</span>
        </button>
      ))}
      {notes.length > 5 && !allNotes && <button className="link-btn small" style={{ padding: '8px 16px' }} onClick={() => setAllNotes(true)}>Show all {notes.length} notes</button>}
      {!data.facts.length && <div className="empty small">Nothing yet. Accept findings from research, or add what you know.</div>}
      {editing && <FactDialog client={client} fact={typeof editing === 'string' ? null : editing} kind={typeof editing === 'string' ? editing : editing.kind}
                              labels={data.metric_labels} onClose={() => setEditing(null)} />}
    </section>
  );
}

function FactDialog({ client, fact, kind: initialKind, labels, onClose }: { client: Client; fact: Fact | null; kind: 'metric' | 'note'; labels: string[]; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [kind, setKind] = useState(initialKind);
  const [f, setF] = useState({ label: fact?.label ?? '', value: fact?.value ?? '', source_url: fact?.source_url ?? '' });
  const done = () => { qc.invalidateQueries({ queryKey: ['client-research', client.id] }); onClose(); };
  const save = useMutation({
    mutationFn: () => fact ? patch(`/api/facts/${fact.id}`, f) : post(`/api/clients/${client.id}/facts`, { ...f, kind, source_url: f.source_url || null }),
    onSuccess: done, onError: (e: Error) => toast(e.message, true),
  });
  const remove = async () => {
    if (await confirm(`Remove "${fact!.label}"?`, { ok: 'Remove', danger: true })) { try { await del(`/api/facts/${fact!.id}`); done(); } catch (e) { toast((e as Error).message, true); } }
  };
  return (
    <Modal title={fact ? fact.label : kind === 'metric' ? 'Add a metric' : 'Add a note'} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {!fact && <div className="seg"><button type="button" className={kind === 'metric' ? 'on' : ''} onClick={() => setKind('metric')}>Metric</button><button type="button" className={kind === 'note' ? 'on' : ''} onClick={() => setKind('note')}>Note</button></div>}
        <label className="field"><span>{kind === 'metric' ? 'Metric' : 'Topic'}</span>
          <input className="input" list="metric-labels" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} maxLength={120} required autoFocus={!fact} placeholder={kind === 'metric' ? 'e.g. Employees' : 'e.g. Current agency'} />
          {kind === 'metric' && <datalist id="metric-labels">{labels.map((l) => <option key={l} value={l} />)}</datalist>}</label>
        <label className="field"><span>{kind === 'metric' ? 'Value' : 'Note'}</span>
          {kind === 'metric' ? <input className="input" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} maxLength={5000} required />
            : <textarea className="input" rows={3} value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} maxLength={5000} required />}</label>
        <label className="field"><span>Source (link)</span><input className="input" type="url" value={f.source_url} onChange={(e) => setF({ ...f, source_url: e.target.value })} placeholder="https://" /></label>
        <div className="row" style={{ gap: 8 }}>
          {fact && <button type="button" className="btn ghost sm danger" onClick={remove}>Remove</button>}
          <span className="grow" /><button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!f.label.trim() || !f.value.trim() || save.isPending}>Save</button>
        </div>
        {fact && <p className="muted tiny" style={{ margin: 0 }}>{fact.from_research ? 'From research' : 'Added'} by {fact.created_by} · {ago(fact.updated_at)}</p>}
      </form>
      {dialog}
    </Modal>
  );
}

// ---------- one session ----------

function SessionView({ id, client, onBack }: { id: string; client: Client; onBack: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const { data: s, error } = useQuery({ queryKey: ['research-session', id], queryFn: () => get<SessionFull>(`/api/research/${id}`),
                                        refetchInterval: (q) => (q.state.data?.busy ? 5000 : false) });
  const [prompt, setPrompt] = useState('');
  const [budget, setBudget] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const turns = s?.turns.length ?? 0;
  useEffect(() => { if (turns > 1) bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [turns]);
  const set = (next: SessionFull) => { qc.setQueryData(['research-session', id], next); qc.invalidateQueries({ queryKey: ['client-research', client.id] }); };
  const act = (fn: () => Promise<SessionFull>) => fn().then(set).catch((e: Error) => toast(e.message, true));
  const ask = useMutation({ mutationFn: () => post<SessionFull>(`/api/research/${id}/ask`, { prompt }), onSuccess: (n) => { set(n); setPrompt(''); }, onError: (e: Error) => toast(e.message, true) });
  if (error) return <div className="card pad"><p className="muted">That research session is gone.</p><button className="btn sm" onClick={onBack}>Back</button></div>;
  if (!s) return <span className="spin muted" />;
  const remove = async () => {
    if (!(await confirm(`Delete "${s.title}"?`, { body: 'The session and its answers go. Metrics, notes and contacts you accepted stay.', ok: 'Delete', danger: true }))) return;
    try { await del(`/api/research/${id}`); qc.invalidateQueries({ queryKey: ['client-research', client.id] }); onBack(); } catch (e) { toast((e as Error).message, true); }
  };
  const out = s.left_usd < 0.05;
  return (
    <div className="stack research" style={{ gap: 14 }}>
      <div className="row wrap" style={{ gap: 8 }}>
        <button className="btn ghost sm" onClick={onBack}>← Research</button>
        <h2 className="grow" style={{ margin: 0, minWidth: 0 }}>{s.title}</h2>
        {budget === null
          ? <button className="link-btn tiny muted" onClick={() => s.can_run && setBudget(String(s.budget_usd))} title={s.can_run ? 'Change the budget' : ''}>{usd(s.cost_usd)} of {usd(s.budget_usd)}</button>
          : <form className="row" style={{ gap: 4 }} onSubmit={(e) => { e.preventDefault(); act(() => patch(`/api/research/${id}`, { budget_usd: Number(budget) })); setBudget(null); }}>
              <span className="tiny muted">Budget $</span><input className="input compact" type="number" min={0.5} max={100} step={0.5} value={budget} onChange={(e) => setBudget(e.target.value)} style={{ width: 80 }} autoFocus />
              <button className="btn sm">Save</button><button type="button" className="btn ghost sm" onClick={() => setBudget(null)}>Cancel</button></form>}
        {s.busy && s.can_run && <button className="btn sm" onClick={() => act(() => post(`/api/research/${id}/stop`, {}))}>Stop</button>}
        {!s.busy && s.can_edit && <button className="btn ghost sm danger" onClick={remove} aria-label="Delete this session"><Icon name="x" className="ico sm" /></button>}
      </div>
      {s.turns.map((t) => <TurnView key={t.id} t={t} s={s} first={t.number === 1} onChange={set} />)}
      {s.can_run && (
        <form className="card pad stack ask-box" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); if (prompt.trim()) ask.mutate(); }}>
          <textarea className="input" rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={4000} disabled={s.busy || out}
                    placeholder={out ? 'This session has used its budget. Raise it to ask more.' : s.busy ? 'Claude is working…' : 'Ask a follow-up, e.g. "Look at their app reviews" or "Who runs IT?"'}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && prompt.trim() && !s.busy) ask.mutate(); }} />
          <div className="row"><span className="muted tiny grow">Continues this session, so Claude remembers what it found. {usd(s.left_usd)} left.</span>
            <button className="btn primary sm" disabled={!prompt.trim() || s.busy || out || ask.isPending}>Ask</button></div>
        </form>
      )}
      <div ref={bottom} />
      {dialog}
    </div>
  );
}

function TurnView({ t, s, first, onChange }: { t: Turn; s: SessionFull; first: boolean; onChange: (s: SessionFull) => void }) {
  const working = t.status === 'queued' || t.status === 'running';
  const searches = t.steps.filter((x) => x.startsWith('Searched')).length, pages = t.steps.filter((x) => x.startsWith('Read')).length;
  return (
    <section className="card turn">
      <div className="turn-q">
        <span className="avatar xs staff">{t.asked_by.slice(0, 1)}</span>
        <div className="grow" style={{ minWidth: 0 }}>
          {first ? <><b className="small">{s.title === 'Company overview' ? 'Company overview' : `Overview, focusing on: ${s.title}`}</b>
                     <details className="tiny muted"><summary>The full question</summary><p style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0' }}>{t.prompt}</p></details></>
                 : <span className="small" style={{ whiteSpace: 'pre-wrap' }}>{t.prompt}</span>}
        </div>
        <span className="muted tiny">{t.status === 'ok' || t.cost_usd ? usd(t.cost_usd) : ''}</span>
      </div>
      {(t.steps.length > 0 || working) && (
        working
          ? <div className="steps live">
              <div className="small"><span className="spin" /> {t.status === 'queued' ? 'Waiting to start…' : `Researching… ${searches} search${searches === 1 ? '' : 'es'}, ${pages} page${pages === 1 ? '' : 's'} read`}</div>
              {t.steps.slice(-4).map((x, i) => <div key={i} className="tiny muted step">{x}</div>)}
            </div>
          : <details className="steps"><summary className="tiny muted">{searches} search{searches === 1 ? '' : 'es'}, {pages} page{pages === 1 ? '' : 's'} read</summary>
              {t.steps.map((x, i) => <div key={i} className="tiny muted step">{x}</div>)}</details>
      )}
      {t.answer_html && <div className="rich-text answer" dangerouslySetInnerHTML={{ __html: t.answer_html }} />}
      {t.status === 'stopped' && <p className="muted small turn-note">Stopped.</p>}
      {t.error && <p className={`small turn-note ${t.status === 'failed' ? 'bad-text' : 'muted'}`}>{t.error}</p>}
      {t.findings.length > 0 && <Findings t={t} s={s} onChange={onChange} />}
    </section>
  );
}

function Findings({ t, s, onChange }: { t: Turn; s: SessionFull; onChange: (s: SessionFull) => void }) {
  const toast = useToast();
  const [editing, setEditing] = useState<{ id: string; label: string; value: string } | null>(null);
  const [showDone, setShowDone] = useState(false);
  const act = (f: Finding, action: string, extra = {}) =>
    post<SessionFull>(`/api/research/${s.id}/turns/${t.id}/findings/${f.id}`, { action, ...extra }).then(onChange).catch((e: Error) => toast(e.message, true));
  const fresh = t.findings.filter((f) => f.state === 'new'), done = t.findings.filter((f) => f.state !== 'new');
  const row = (f: Finding) => (
    <div key={f.id} className={`finding ${f.state}`}>
      <span className="badge tiny-badge">{KIND[f.kind]}</span>
      {editing?.id === f.id
        ? <form className="grow row wrap" style={{ gap: 6 }} onSubmit={(e) => { e.preventDefault(); act(f, 'accept', { label: editing.label, value: editing.value }); setEditing(null); }}>
            <input className="input compact" value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} maxLength={120} style={{ width: 150 }} aria-label="Label" />
            <input className="input compact grow" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} maxLength={2000} style={{ minWidth: 140 }} aria-label="Value" />
            <button className="btn sm primary">Keep</button><button type="button" className="btn sm ghost" onClick={() => setEditing(null)}>Cancel</button>
          </form>
        : <span className="grow small" style={{ minWidth: 0 }}><b>{f.label}</b>{f.kind === 'person' ? ', ' : ': '}{f.value} <Source url={f.source} checked={f.checked} /></span>}
      {f.state === 'new' && s.can_edit && editing?.id !== f.id && <span className="row finding-actions" style={{ gap: 4 }}>
        <button className="btn sm" onClick={() => act(f, 'accept')} title={f.kind === 'person' ? 'Add as a contact' : f.kind === 'metric' ? 'Save as a metric' : 'Save as a note'}>Keep</button>
        <button className="btn sm ghost" onClick={() => setEditing({ id: f.id, label: f.label, value: f.value })} aria-label="Edit, then keep"><Icon name="edit" className="ico sm" /></button>
        <button className="btn sm ghost" onClick={() => act(f, 'dismiss')} aria-label="Dismiss"><Icon name="x" className="ico sm" /></button>
      </span>}
      {f.state === 'accepted' && <span className="tiny ok-text">{f.kind === 'person' ? 'Contact added' : 'Saved'}</span>}
      {f.state === 'dismissed' && s.can_edit && <button className="link-btn tiny" onClick={() => act(f, 'restore')}>Undo</button>}
    </div>
  );
  return (
    <div className="findings">
      <div className="row tiny muted" style={{ gap: 8 }}><b className="grow">Findings</b>{fresh.length > 0 ? `${fresh.length} to review` : 'All reviewed'}
        {done.length > 0 && <button className="link-btn tiny" onClick={() => setShowDone(!showDone)}>{showDone ? 'Hide' : 'Show'} {done.length} reviewed</button>}</div>
      {fresh.map(row)}
      {showDone && done.map(row)}
    </div>
  );
}
